import React, { useEffect, useMemo, useRef, useState } from "react";

export const ROLE_OPTIONS = [
  { value: "AR", label: "ARs", title: "Assault Rifles" },
  { value: "SMG", label: "SMGs", title: "Sub-Machine Guns" },
  { value: "Heavy", label: "Heavy", title: "Shotgun / LMG" },
  { value: "Marksman", label: "Marks", title: "Sniper / Marksman" },
];

export const ROLE_LIMITS = { AR: 3, SMG: 3, Heavy: 2, Marksman: 2 };

export const buildInitialPlayerAssignments = (maxSteps = 10) => {
  const initial = { global: [] };
  for (let i = 1; i <= maxSteps; i++) {
    initial[i] = [];
  }
  return initial;
};

export const usePlayerAssignments = (currentRoster, maxSteps = 10) => {
  const [playerAssignments, setPlayerAssignments] = useState(() =>
    buildInitialPlayerAssignments(maxSteps),
  );

  const activeAssignments = playerAssignments.global || [];

  const assignmentRows = useMemo(() => {
    if (activeAssignments.length) return activeAssignments;
    if (!currentRoster.length) return [];
    const basePlayers = currentRoster.slice(0, 5);
    return basePlayers.map((p) => ({
      ...p,
      role: "Entry",
      tactical: "Smoke",
      lethal: "Frag",
      roleA: "",
      roleB: "",
    }));
  }, [activeAssignments, currentRoster]);

  const roleCounts = useMemo(() => {
    const counts = { AR: 0, SMG: 0, Heavy: 0, Marksman: 0 };
    assignmentRows.forEach((row) => {
      if (row.roleA && counts[row.roleA] !== undefined) counts[row.roleA] += 1;
      if (row.roleB && counts[row.roleB] !== undefined) counts[row.roleB] += 1;
    });
    return counts;
  }, [assignmentRows]);

  const updateAssignment = (stepId, playerIndex, updates) => {
    setPlayerAssignments((prev) => {
      const existing =
        prev.global && prev.global.length
          ? prev.global
          : currentRoster.map((p) => ({
              ...p,
              role: "Entry",
              tactical: "Smoke",
              lethal: "Frag",
              roleA: "",
              roleB: "",
            }));

      const next = existing.map((row, idx) => {
        if (idx !== playerIndex) return row;
        const merged = { ...row, ...updates };
        if (merged.roleA && merged.roleA === merged.roleB) {
          merged.roleB = "";
        }
        return merged;
      });

      return {
        ...prev,
        [stepId]: next,
        global: stepId === "global" ? next : prev.global || next,
      };
    });
  };

  return {
    playerAssignments,
    assignmentRows,
    roleCounts,
    updateAssignment,
    setPlayerAssignments,
  };
};

export const useAssignmentsDrag = (initialPos = { x: 16, y: 16 }) => {
  const [assignPanelPos, setAssignPanelPos] = useState(initialPos);
  const assignDragging = useRef(false);

  useEffect(() => {
    const handleMove = (e) => {
      if (!assignDragging.current) return;
      setAssignPanelPos((pos) => ({
        x: Math.max(8, pos.x + e.movementX),
        y: Math.max(8, pos.y + e.movementY),
      }));
    };
    const handleUp = () => {
      assignDragging.current = false;
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  const handleDragStart = (e) => {
    e.preventDefault();
    assignDragging.current = true;
  };

  return { assignPanelPos, handleDragStart, setAssignPanelPos };
};

export const AssignmentsOverlay = ({
  show,
  assignPanelPos,
  onDragStart,
  assignmentRows,
  roleCounts,
  roleLimits = ROLE_LIMITS,
  updateAssignment,
}) => {
  if (!show) return null;

  return (
    <div
      className="assignments-floating"
      style={{ left: assignPanelPos.x, top: assignPanelPos.y }}
    >
      <div
        className="assignments-floating-header"
        onMouseDown={onDragStart}
      >
        <div>
          <div className="section-title">Player Assignments</div>
          <div className="section-subtitle">Roles and utility per player</div>
        </div>
        <span className="drag-hint" title="Drag to move">
         
        </span>
      </div>
      <div className="assignments-table tight">
        <div className="assignments-head">
          <span title="Player">Player</span>
          <span title="Primary weapon class">Role A</span>
          <span title="Secondary weapon class">Role B</span>
          <span title="Tactical">Tac</span>
          <span title="Lethal">Leth</span>
        </div>
        {assignmentRows.length === 0 && (
          <div className="empty-row">
            Add players to your roster to assign roles
          </div>
        )}
        {assignmentRows.map((row, idx) => (
          <div className="assignments-row" key={`${row.name}-${idx}`}>
            <div className="player-cell">
              {row.avatar && (
                <img
                  src={row.avatar}
                  alt={row.name}
                  className="player-avatar small"
                />
              )}
              <span>{row.name}</span>
            </div>
            <select
              className="compact-select"
              value={row.roleA || ""}
              onChange={(e) =>
                updateAssignment("global", idx, {
                  roleA: e.target.value,
                })
              }
            >
              <option value="">--</option>
              {ROLE_OPTIONS.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  title={opt.title}
                  disabled={
                    roleCounts[opt.value] >= roleLimits[opt.value] &&
                    row.roleA !== opt.value
                  }
                >
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              className="compact-select"
              value={row.roleB || ""}
              onChange={(e) =>
                updateAssignment("global", idx, {
                  roleB: e.target.value,
                })
              }
            >
              <option value="">--</option>
              {ROLE_OPTIONS.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  title={opt.title}
                  disabled={
                    roleCounts[opt.value] >= roleLimits[opt.value] &&
                    row.roleB !== opt.value
                  }
                >
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              className="compact-select"
              value={row.tactical || "Smoke"}
              onChange={(e) =>
                updateAssignment("global", idx, {
                  tactical: e.target.value,
                })
              }
            >
              <option value="Smoke">Smoke</option>
              <option value="Trophy">Trophy</option>
              <option value="Flash">Flash</option>
              <option value="EMP">EMP</option>
            </select>
            <select
              className="compact-select"
              value={row.lethal || "Frag"}
              onChange={(e) =>
                updateAssignment("global", idx, {
                  lethal: e.target.value,
                })
              }
            >
              <option value="Frag">Frag</option>
              <option value="Molotov">Molotov</option>
              <option value="Semtex">Semtex</option>
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};
